import * as z from 'zod';

// Mirrors api/src/features/tracking/TrackingInteraction.ts. Keep the two in
// step: the server validates with a discriminated union, so an event this file
// allows but the server does not is a 400 and a silently lost event.
export const PAGE_VIEW_EVENT_TYPE = 'visibleOnScreen';

export const TrackingEventType = z.enum([
    'widgetRendered',
    'engaged',
    'visibleOnScreen',
    'screenExit',
    'fieldInteraction',
    'validationFailed',
    'serviceResult',
    'outcome',
    'sessionEnded',
]);

export type TrackingEventType = z.infer<typeof TrackingEventType>;

// Which host served the widget: ours in production, a non-production one, or the
// partner's own page for the code snippet. Not how it was embedded — that is
// TrackingEmbedMode. See trackingSource.ts.
export const TrackingSource = z.enum(['iframe', 'codesnippet', 'test']);

export type TrackingSource = z.infer<typeof TrackingSource>;

// How the widget is embedded: inside a partner <iframe>, rendered into the
// partner's DOM by the code snippet, or top-level on one of our own hosts (SMS
// and QR links, Dealer Studio hand-offs, direct visits).
export const TrackingEmbedMode = z.enum(['iframe', 'snippet', 'direct']);

export type TrackingEmbedMode = z.infer<typeof TrackingEmbedMode>;

// Where embedOrigin/embedPath came from, so a report can tell an exact page from
// an origin with a bare "/". Only urlParam and location are exact.
export const TrackingEmbedSource = z.enum(['urlParam', 'location', 'referrer', 'ancestorOrigin']);

export type TrackingEmbedSource = z.infer<typeof TrackingEmbedSource>;

// Whether the surface was a full screen or a modal. Previously lost, because
// only the middle segment of the element id was kept and 'screen-login' and
// 'modal-login' both recorded as 'login'.
export const TrackingSurface = z.enum(['screen', 'modal']);

export type TrackingSurface = z.infer<typeof TrackingSurface>;

// How the applicant got in. Set once at session start.
export const TrackingEntryMode = z.enum(['new', 'recognised', 'loggedIn', 'signedUp']);

export type TrackingEntryMode = z.infer<typeof TrackingEntryMode>;

export const TrackingOutcomeType = z.enum([
    'approved',
    'referred',
    'declined',
    'servicingFailed',
    'ineligible',
    'abandoned',
    'systemFailed',
]);

export type TrackingOutcomeType = z.infer<typeof TrackingOutcomeType>;

export const TrackingServiceResult = z.enum(['success', 'failure', 'timeout']);

export type TrackingServiceResult = z.infer<typeof TrackingServiceResult>;

// What a blur tells us. There is deliberately no value field: we record that a
// field was used, never what was typed into it.
export const TrackingFieldStatus = z.enum(['completed', 'skipped', 'cleared', 'prefilled']);

export type TrackingFieldStatus = z.infer<typeof TrackingFieldStatus>;

export const TrackingUserDevice = z.object({
    deviceType: z.enum(['mobile', 'tablet', 'desktop']).optional(),
    browser: z.string().optional(),
    os: z.string().optional(),
    screenSize: z.string().optional(),
});

export type TrackingUserDevice = z.infer<typeof TrackingUserDevice>;

// The last repayment estimate the quick calculator showed the applicant. See
// calculatorEstimate.ts, whose own CalculatorEstimateSnapshot type is named
// differently on purpose: both files are re-exported through index.ts with
// `export *`, and a type and a value sharing one name there is a TS2308
// ambiguity that breaks the build.
export const TrackingCalculatorEstimate = z.object({
    purchasePrice: z.number(),
    loanTermYears: z.number(),
    loanUsage: z.enum(['PERSONAL', 'BUSINESS', 'NOVATED']),
    weeklyRepayment: z.number(),
    advertisedRate: z.number().optional(),
    comparisonRate: z.number().optional(),
});

export type TrackingCalculatorEstimate = z.infer<typeof TrackingCalculatorEstimate>;

// The timestamp is stamped server side and so is not sent from here. Durations
// are the exception: dwellMs is measured in the browser, because the gap
// between two server receipt times includes network latency and the batching
// interval, which would turn a three second field into eleven.
export const TrackingEvent = z.object({
    type: TrackingEventType,

    // Monotonic within the session, stamped when the event happens. The path is
    // reconstructed from this, not from the timestamp: batching gives a whole
    // batch one timestamp and batches can arrive out of order.
    seq: z.number().optional(),

    screen: z.string().optional(),
    parentScreen: z.string().optional(),
    surface: TrackingSurface.optional(),
    status: z.string().optional(),
    financeType: z.string().optional(),
    vehicleCondition: z.string().optional(),

    // fieldInteraction / validationFailed
    field: z.string().optional(),
    fieldStatus: TrackingFieldStatus.optional(),
    editCount: z.number().optional(),
    errorCode: z.string().optional(),

    // screenExit / fieldInteraction / serviceResult
    dwellMs: z.number().optional(),
    durationMs: z.number().optional(),

    // serviceResult
    service: z.string().optional(),
    result: TrackingServiceResult.optional(),

    // outcome
    outcomeType: TrackingOutcomeType.optional(),
    reason: z.string().optional(),

    // sessionEnded
    endReason: z.string().optional(),
    lastScreen: z.string().optional(),
    lastField: z.string().optional(),

    // engaged
    trigger: z.string().optional(),
});

export type TrackingEvent = z.infer<typeof TrackingEvent>;

export const TrackingRequest = z.object({
    sessionId: z.string(),
    partnerId: z.string(), // Foreign key
    storeId: z.string(), // Foreign key
    brandId: z.string(), // Foreign key
    organisationId: z.string(), // Foreign key
    templateId: z.string().optional(),

    // The vroom code from the URL, eg M6307. The human-recognisable identifier;
    // partnerId is a Mongo _id.
    vroomCode: z.string().optional(),

    // The partner page the widget is embedded on. Path only, never the query
    // string — see embedPage.ts.
    embedOrigin: z.string().optional(),
    embedPath: z.string().optional(),
    embedMode: TrackingEmbedMode.optional(),
    embedSource: TrackingEmbedSource.optional(),

    // The vehicle the partner put on the iframe src. On a dealer site this
    // identifies the listing page even when the path is unavailable. A strict
    // whitelist — see listingIdentity.ts.
    listingVin: z.string().optional(),
    listingStockNumber: z.string().optional(),
    listingNvic: z.string().optional(),

    // The Glass's Guide body of the vehicle the user resolved in the journey,
    // eg '4D WAGON'. See trackingVehicle.ts.
    vehicleBodyType: z.string().optional(),

    // The last repayment estimate the quick calculator showed, if any. See
    // calculatorEstimate.ts.
    calculatorEstimate: TrackingCalculatorEstimate.optional(),

    // The device's IANA time zone. The only location signal available for a
    // visitor who leaves before entering an address.
    timeZone: z.string().optional(),

    // The join to the application record, and through it to the Manage lead.
    applicationId: z.string().optional(),

    // Sent as the raw mobile and hashed server side. It is never stored in the
    // clear and never comes back out.
    mobilePhone: z.string().optional(),

    userFlow: z.string().optional(),
    source: TrackingSource,
    configVersion: z.string().optional(),

    entryMode: TrackingEntryMode.optional(),
    isResumed: z.boolean().optional(),
    resumedAtStage: z.string().optional(),
    loginFromScreen: z.string().optional(),
    isInternal: z.boolean().optional(),

    userDevice: TrackingUserDevice.optional(),

    events: z.array(TrackingEvent).min(1),
});

export type TrackingRequest = z.infer<typeof TrackingRequest>;

// Retained so existing imports of the old single-interaction name still resolve.
export type TrackingInteraction = TrackingEvent;
